/**
 * Commercial Action Orchestration — Phase 3
 * 
 * Execution Learning Loop
 * 
 * Purpose: Answer "What is the single highest-value commercial move right now?"
 * Phase 3 adds: Outcome tracking, learning metrics, dismissal pattern analysis.
 * 
 * Success metric: Would you actually do what REX says next?
 */

import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { API } from "@/lib/constants";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Zap, Target, Clock, User, AlertTriangle, CheckCircle2,
  ChevronDown, ChevronUp, ArrowRight, TrendingUp,
  Lightbulb, X, RefreshCw, BookMarked, BarChart3,
  ListChecks, Filter, Brain, Activity, Award, XCircle,
  Timer, MessageSquare, Sparkles,
} from "lucide-react";

const CATEGORY_CONFIG = {
  opportunity_progression: { 
    label: "Opportunity Progression", 
    shortLabel: "Progression",
    color: "text-emerald-400", 
    bg: "bg-emerald-900/20", 
    border: "border-emerald-700/30",
    icon: TrendingUp,
  },
  expansion_reactivation: { 
    label: "Expansion & Reactivation", 
    shortLabel: "Expansion",
    color: "text-violet-400", 
    bg: "bg-violet-900/20", 
    border: "border-violet-700/30",
    icon: RefreshCw,
  },
  proof_asset: { 
    label: "Proof & Asset", 
    shortLabel: "Proof",
    color: "text-sky-400", 
    bg: "bg-sky-900/20", 
    border: "border-sky-700/30",
    icon: BookMarked,
  },
  thought_leadership: { 
    label: "Thought Leadership", 
    shortLabel: "Thought",
    color: "text-amber-400", 
    bg: "bg-amber-900/20", 
    border: "border-amber-700/30",
    icon: Lightbulb,
  },
  risk_recovery: { 
    label: "Risk & Recovery", 
    shortLabel: "Risk",
    color: "text-red-400", 
    bg: "bg-red-900/20", 
    border: "border-red-700/30",
    icon: AlertTriangle,
  },
  hold: { 
    label: "On Hold", 
    shortLabel: "Hold",
    color: "text-slate-400", 
    bg: "bg-slate-800/40", 
    border: "border-slate-700/30",
    icon: Clock,
  },
};

const OUTCOME_CONFIG = {
  converted: { label: "Converted", color: "text-emerald-400", bg: "bg-emerald-900/30", icon: Award },
  stalled: { label: "Stalled", color: "text-amber-400", bg: "bg-amber-900/30", icon: Clock },
  lost: { label: "Lost", color: "text-red-400", bg: "bg-red-900/30", icon: XCircle },
  in_progress: { label: "In Progress", color: "text-sky-400", bg: "bg-sky-900/30", icon: Activity },
};

const ALL_CATEGORIES = ["opportunity_progression", "expansion_reactivation", "proof_asset", "thought_leadership", "risk_recovery", "hold"];

function SectionLabel({ children }) {
  return <p className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-1">{children}</p>;
}

function StatCard({ label, value, subValue, icon: Icon, color = "text-slate-200", onClick }) {
  return (
    <div 
      className={`bg-[#111827] border border-slate-800/60 rounded-lg p-4 ${onClick ? "cursor-pointer hover:border-slate-700/60 transition-all" : ""}`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
        {Icon && <Icon className={`w-4 h-4 ${color}`} />}
      </div>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
      {subValue && <p className="text-xs text-slate-500 mt-1">{subValue}</p>}
    </div>
  );
}

export default function ActionsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [learning, setLearning] = useState(null);
  const [queue, setQueue] = useState([]);
  const [pendingOutcomes, setPendingOutcomes] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [showAllQueue, setShowAllQueue] = useState(false);
  const [showLearning, setShowLearning] = useState(false);
  const [showPendingOutcomes, setShowPendingOutcomes] = useState(false);
  
  // Modals
  const [dismissReason, setDismissReason] = useState("");
  const [showDismissModal, setShowDismissModal] = useState(false);
  const [dismissingAction, setDismissingAction] = useState(null);
  const [showOutcomeModal, setShowOutcomeModal] = useState(false);
  const [outcomeActionId, setOutcomeActionId] = useState(null);
  const [selectedOutcome, setSelectedOutcome] = useState("");
  const [outcomeNotes, setOutcomeNotes] = useState("");
  
  const [actionInProgress, setActionInProgress] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const categoryParam = selectedCategory ? `&category=${selectedCategory}` : "";
      const [statsRes, queueRes, learningRes, pendingRes] = await Promise.all([
        fetch(`${API}/actions/stats`, { credentials: "include" }),
        fetch(`${API}/actions/queue?limit=10${categoryParam}`, { credentials: "include" }),
        fetch(`${API}/actions/learning`, { credentials: "include" }),
        fetch(`${API}/actions/pending-outcomes`, { credentials: "include" }),
      ]);
      
      if (statsRes.ok) setStats(await statsRes.json());
      if (queueRes.ok) {
        const data = await queueRes.json();
        setQueue(data.queue || []);
      }
      if (learningRes.ok) setLearning(await learningRes.json());
      if (pendingRes.ok) {
        const data = await pendingRes.json();
        setPendingOutcomes(data.pending_outcomes || []);
      }
    } catch {
      toast.error("Failed to load action data");
    } finally {
      setLoading(false);
    }
  }, [selectedCategory]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAccept = async (action) => {
    setActionInProgress(true);
    try {
      const res = await fetch(`${API}/actions/${action.action_id}/accept`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          notes: "",
          account_name: action.account_name || "",
          recommended_action: action.recommended_action || "",
          action_category: action.action_category || "",
        }),
      });
      if (res.ok) {
        toast.success("Action accepted — go execute!");
        if (action.brief_id) {
          navigate(`/commercial-briefs/${action.brief_id}`);
        } else {
          fetchData();
        }
      } else {
        toast.error("Failed to accept action");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setActionInProgress(false);
    }
  };

  const handleDismiss = async () => {
    if (!dismissingAction || !dismissReason.trim()) return;
    setActionInProgress(true);
    try {
      const res = await fetch(`${API}/actions/${dismissingAction.action_id}/dismiss`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason: dismissReason }),
      });
      if (res.ok) {
        toast.success("Action dismissed — reason logged");
        setShowDismissModal(false);
        setDismissReason("");
        setDismissingAction(null);
        fetchData();
      } else {
        toast.error("Failed to dismiss action");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setActionInProgress(false);
    }
  };

  const handleRecordOutcome = async () => {
    if (!outcomeActionId || !selectedOutcome) return;
    setActionInProgress(true);
    try {
      const res = await fetch(`${API}/actions/${outcomeActionId}/outcome`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          outcome: selectedOutcome,
          notes: outcomeNotes,
        }),
      });
      if (res.ok) {
        toast.success(`Outcome '${selectedOutcome}' recorded — REX is learning!`);
        setShowOutcomeModal(false);
        setOutcomeActionId(null);
        setSelectedOutcome("");
        setOutcomeNotes("");
        fetchData();
      } else {
        toast.error("Failed to record outcome");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setActionInProgress(false);
    }
  };

  const openDismissModal = (action) => {
    setDismissingAction(action);
    setShowDismissModal(true);
  };

  const openOutcomeModal = (actionId) => {
    setOutcomeActionId(actionId);
    setShowOutcomeModal(true);
  };

  const topAction = queue[0];
  const remainingQueue = queue.slice(1, showAllQueue ? 10 : 5);

  const learningStatusColor = {
    strong: "text-emerald-400",
    growing: "text-sky-400",
    early: "text-amber-400",
    starting: "text-slate-400",
  };

  return (
    <div className="min-h-screen bg-[#0a0e17] text-slate-200" data-testid="actions-page">
      {/* Header */}
      <div className="border-b border-slate-800/60 bg-[#0d1220]">
        <div className="max-w-[1200px] mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2.5 mb-1">
                <Zap className="w-5 h-5 text-amber-500" />
                <h1 className="text-lg font-bold tracking-tight text-slate-100" style={{ fontFamily: "Manrope, sans-serif" }} data-testid="page-title">
                  Action Queue
                </h1>
                <Badge variant="outline" className="text-[9px] border-amber-700/50 text-amber-400 ml-1">BD OPERATIONS</Badge>
              </div>
              <p className="text-xs text-slate-500">
                Ranked commercial actions by priority. Execute the highest-value moves first.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                size="sm" 
                variant={showLearning ? "default" : "ghost"}
                className={showLearning ? "bg-violet-600 hover:bg-violet-700 text-white" : "text-slate-500"}
                onClick={() => setShowLearning(!showLearning)}
              >
                <Brain className="w-3.5 h-3.5 mr-1.5" /> Learning
              </Button>
              <Button size="sm" variant="ghost" className="text-slate-500" onClick={fetchData} disabled={loading}>
                <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-[1200px] mx-auto px-6 py-6">
        {/* Learning Insights Panel */}
        {showLearning && learning && (
          <div className="mb-6 rounded-xl border border-violet-700/30 bg-violet-900/10 p-5" data-testid="learning-panel">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Brain className="w-5 h-5 text-violet-400" />
                <h2 className="text-base font-bold text-slate-100" style={{ fontFamily: "Manrope, sans-serif" }}>
                  Learning Insights
                </h2>
                <Badge className={`text-[9px] ${learningStatusColor[learning.learning_status] || "text-slate-400"} bg-transparent border border-current`}>
                  {learning.learning_status?.toUpperCase()}
                </Badge>
              </div>
              <p className="text-xs text-slate-500">{learning.learning_message}</p>
            </div>
            
            {/* Learning Metrics Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
              <div className="bg-slate-900/50 rounded-lg p-3">
                <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Acceptance Rate</p>
                <p className="text-xl font-bold text-emerald-400">{learning.acceptance_rate}%</p>
                <p className="text-[10px] text-slate-500">{learning.total_accepted} of {learning.total_accepted + learning.total_dismissed} decisions</p>
              </div>
              <div className="bg-slate-900/50 rounded-lg p-3">
                <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Conversion Rate</p>
                <p className="text-xl font-bold text-sky-400">{learning.conversion_rate}%</p>
                <p className="text-[10px] text-slate-500">{learning.outcome_breakdown?.converted || 0} converted</p>
              </div>
              <div className="bg-slate-900/50 rounded-lg p-3">
                <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Avg Time to Outcome</p>
                <p className="text-xl font-bold text-amber-400">{learning.avg_days_to_outcome} days</p>
                <p className="text-[10px] text-slate-500">{learning.total_outcomes_recorded} outcomes recorded</p>
              </div>
              <div 
                className="bg-slate-900/50 rounded-lg p-3 cursor-pointer hover:bg-slate-800/50 transition-all"
                onClick={() => setShowPendingOutcomes(!showPendingOutcomes)}
              >
                <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Pending Feedback</p>
                <p className="text-xl font-bold text-violet-400">{learning.pending_outcomes_count}</p>
                <p className="text-[10px] text-slate-500">Click to view</p>
              </div>
            </div>
            
            {/* Outcome Breakdown */}
            <div className="grid grid-cols-2 gap-4 mb-5">
              <div>
                <p className="text-xs font-semibold text-slate-400 mb-2">Outcome Breakdown</p>
                <div className="space-y-2">
                  {Object.entries(OUTCOME_CONFIG).map(([key, config]) => {
                    const count = learning.outcome_breakdown?.[key] || 0;
                    const Icon = config.icon;
                    return (
                      <div key={key} className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2">
                          <Icon className={`w-3.5 h-3.5 ${config.color}`} />
                          <span className="text-slate-300">{config.label}</span>
                        </div>
                        <span className={config.color}>{count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              <div>
                <p className="text-xs font-semibold text-slate-400 mb-2">Top Dismissal Reasons</p>
                <div className="space-y-2">
                  {learning.top_dismissal_reasons?.length > 0 ? (
                    learning.top_dismissal_reasons.slice(0, 4).map((item, i) => (
                      <div key={i} className="flex items-center justify-between text-xs">
                        <span className="text-slate-400 truncate max-w-[180px]">{item.reason}</span>
                        <span className="text-slate-500">{item.count}</span>
                      </div>
                    ))
                  ) : (
                    <p className="text-xs text-slate-500">No dismissals yet</p>
                  )}
                </div>
              </div>
            </div>
            
            {/* Pending Outcomes List */}
            {showPendingOutcomes && pendingOutcomes.length > 0 && (
              <div className="border-t border-slate-800/60 pt-4">
                <p className="text-xs font-semibold text-slate-400 mb-3">Actions Awaiting Outcome</p>
                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                  {pendingOutcomes.map((item) => {
                    const catConfig = CATEGORY_CONFIG[item.action_category] || {};
                    return (
                      <div key={item.action_id} className="flex items-center justify-between bg-slate-900/50 rounded-lg p-3 gap-3">
                        <div className="flex-1 min-w-0">
                          {item.account_name ? (
                            <>
                              <p className="text-xs font-semibold text-slate-200 truncate">{item.account_name}</p>
                              <p className="text-[10px] text-slate-400 truncate">{item.recommended_action}</p>
                            </>
                          ) : (
                            <p className="text-xs text-slate-300">Action ID: {item.action_id}</p>
                          )}
                          <p className="text-[10px] text-slate-500">
                            Accepted {item.days_since_accept} days ago by {item.accepted_by}
                          </p>
                        </div>
                        <Button 
                          size="sm" 
                          className="h-7 text-xs bg-violet-600 hover:bg-violet-700 shrink-0"
                          onClick={() => openOutcomeModal(item.action_id)}
                        >
                          Record Outcome
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="stats-cards">
            <StatCard 
              label="Pending Actions" 
              value={stats.total_pending} 
              icon={ListChecks}
              color="text-amber-400"
            />
            <StatCard 
              label="Avg Priority" 
              value={stats.avg_priority_score} 
              subValue={`${stats.urgency_breakdown?.high || 0} high urgency`}
              icon={BarChart3}
              color="text-sky-400"
            />
            <StatCard 
              label="Accepted This Week" 
              value={stats.weekly_accepted} 
              icon={CheckCircle2}
              color="text-emerald-400"
            />
            <StatCard 
              label="Pending Feedback" 
              value={pendingOutcomes.length}
              subValue="outcomes to record"
              icon={MessageSquare}
              color="text-violet-400"
              onClick={() => { setShowLearning(true); setShowPendingOutcomes(true); }}
            />
          </div>
        )}

        {/* Category Filter Tabs */}
        <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2" data-testid="category-tabs">
          <div className="flex items-center gap-1 text-xs text-slate-500 mr-2">
            <Filter className="w-3.5 h-3.5" />
            <span>Filter:</span>
          </div>
          <button
            onClick={() => setSelectedCategory("")}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
              selectedCategory === "" 
                ? "bg-amber-600 text-white" 
                : "bg-slate-800 text-slate-400 hover:bg-slate-700"
            }`}
            data-testid="filter-all"
          >
            All ({stats?.total_pending || 0})
          </button>
          {ALL_CATEGORIES.map(cat => {
            const config = CATEGORY_CONFIG[cat];
            const count = stats?.by_category?.[cat] || 0;
            if (count === 0) return null;
            return (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all flex items-center gap-1.5 ${
                  selectedCategory === cat 
                    ? `${config.bg} ${config.color} ${config.border} border` 
                    : "bg-slate-800 text-slate-400 hover:bg-slate-700"
                }`}
                data-testid={`filter-${cat}`}
              >
                {config.shortLabel} ({count})
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-32">
            <div className="animate-pulse text-slate-500 text-sm">Analyzing commercial landscape...</div>
          </div>
        ) : queue.length === 0 ? (
          <EmptyState selectedCategory={selectedCategory} setSelectedCategory={setSelectedCategory} />
        ) : (
          <div className="space-y-4">
            {/* Hero Card - Top Action */}
            {topAction && (
              <ActionCard 
                action={topAction} 
                isHero={true}
                onAccept={() => handleAccept(topAction)}
                onDismiss={() => openDismissModal(topAction)}
                actionInProgress={actionInProgress}
                navigate={navigate}
              />
            )}

            {/* Secondary Queue */}
            {remainingQueue.length > 0 && (
              <div className="space-y-2" data-testid="secondary-queue">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Next in Queue</p>
                  {queue.length > 5 && (
                    <button 
                      onClick={() => setShowAllQueue(!showAllQueue)}
                      className="text-xs text-slate-500 hover:text-slate-400 flex items-center gap-1"
                    >
                      {showAllQueue ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                      {showAllQueue ? "Show less" : `Show all ${queue.length}`}
                    </button>
                  )}
                </div>
                {remainingQueue.map((action) => (
                  <ActionCard 
                    key={action.action_id}
                    action={action} 
                    isHero={false}
                    onAccept={() => handleAccept(action)}
                    onDismiss={() => openDismissModal(action)}
                    actionInProgress={actionInProgress}
                    navigate={navigate}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Dismiss Modal */}
      {showDismissModal && dismissingAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-[#111827] border border-slate-800/60 rounded-xl w-full max-w-md mx-4 p-5 shadow-2xl">
            <h3 className="text-base font-bold text-slate-100 mb-2" style={{ fontFamily: "Manrope, sans-serif" }}>
              Dismiss Recommendation
            </h3>
            <p className="text-xs text-slate-500 mb-2">
              <span className="text-slate-400">{dismissingAction.account_name}</span>
            </p>
            <p className="text-xs text-slate-500 mb-4">
              Help REX learn — why is this not the right action?
            </p>
            <textarea
              className="w-full bg-slate-900 border border-slate-700/60 rounded-md px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-slate-600 resize-none mb-4"
              rows={3}
              placeholder="e.g., Already actioned, timing not right, relationship context you're not seeing..."
              value={dismissReason}
              onChange={e => setDismissReason(e.target.value)}
              autoFocus
              data-testid="dismiss-reason-input"
            />
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                className="flex-1 text-slate-500"
                onClick={() => { setShowDismissModal(false); setDismissReason(""); setDismissingAction(null); }}
              >
                Cancel
              </Button>
              <Button 
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                onClick={handleDismiss}
                disabled={!dismissReason.trim() || actionInProgress}
                data-testid="confirm-dismiss-btn"
              >
                {actionInProgress ? "Dismissing..." : "Dismiss Action"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Outcome Recording Modal */}
      {showOutcomeModal && outcomeActionId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-[#111827] border border-slate-800/60 rounded-xl w-full max-w-md mx-4 p-5 shadow-2xl" data-testid="outcome-modal">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-violet-400" />
              <h3 className="text-base font-bold text-slate-100" style={{ fontFamily: "Manrope, sans-serif" }}>
                Record Outcome
              </h3>
            </div>
            <p className="text-xs text-slate-500 mb-4">
              What happened after you executed this action? This helps REX learn which recommendations drive results.
            </p>
            
            {/* Outcome Selection */}
            <div className="grid grid-cols-2 gap-2 mb-4">
              {Object.entries(OUTCOME_CONFIG).map(([key, config]) => {
                const Icon = config.icon;
                return (
                  <button
                    key={key}
                    onClick={() => setSelectedOutcome(key)}
                    className={`p-3 rounded-lg border transition-all flex items-center gap-2 ${
                      selectedOutcome === key
                        ? `${config.bg} ${config.color} border-current`
                        : "bg-slate-900 border-slate-700/60 text-slate-400 hover:border-slate-600"
                    }`}
                    data-testid={`outcome-${key}`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="text-sm font-medium">{config.label}</span>
                  </button>
                );
              })}
            </div>
            
            {/* Notes */}
            <textarea
              className="w-full bg-slate-900 border border-slate-700/60 rounded-md px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-slate-600 resize-none mb-4"
              rows={2}
              placeholder="Optional notes about the outcome..."
              value={outcomeNotes}
              onChange={e => setOutcomeNotes(e.target.value)}
            />
            
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                className="flex-1 text-slate-500"
                onClick={() => { setShowOutcomeModal(false); setOutcomeActionId(null); setSelectedOutcome(""); setOutcomeNotes(""); }}
              >
                Cancel
              </Button>
              <Button 
                className="flex-1 bg-violet-600 hover:bg-violet-700 text-white"
                onClick={handleRecordOutcome}
                disabled={!selectedOutcome || actionInProgress}
                data-testid="confirm-outcome-btn"
              >
                {actionInProgress ? "Recording..." : "Record Outcome"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ActionCard({ action, isHero, onAccept, onDismiss, actionInProgress, navigate }) {
  const config = CATEGORY_CONFIG[action.action_category] || CATEGORY_CONFIG.opportunity_progression;
  const Icon = config.icon || Target;

  if (isHero) {
    return (
      <div className={`rounded-xl border-2 ${config.border} ${config.bg} p-6 shadow-lg`} data-testid="hero-recommendation">
        {/* Header */}
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg ${config.bg} border ${config.border} flex items-center justify-center`}>
              <Icon className={`w-5 h-5 ${config.color}`} />
            </div>
            <div>
              <Badge className={`text-[9px] ${config.bg} ${config.color} ${config.border} border mb-1`}>
                {config.label}
              </Badge>
              <p className="text-xs text-slate-500">
                Priority Score: <span className="font-semibold text-slate-300">{action.priority_score}</span>
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-500">Confidence</p>
            <p className={`text-sm font-semibold ${action.confidence_level === "High" ? "text-emerald-400" : action.confidence_level === "Medium" ? "text-amber-400" : "text-slate-400"}`}>
              {action.confidence_level}
            </p>
          </div>
        </div>

        {/* Account & Action */}
        <div className="mb-5">
          <p className="text-xs text-slate-500 mb-1">Account</p>
          <p className="text-lg font-bold text-slate-100 mb-2" style={{ fontFamily: "Manrope, sans-serif" }}>
            {action.account_name}
          </p>
          <p className="text-base text-slate-200 leading-relaxed">
            {action.recommended_action}
          </p>
        </div>

        {/* Why Now */}
        <div className="rounded-lg bg-slate-900/50 border border-slate-800/60 p-4 mb-5">
          <div className="flex items-start gap-3">
            <Lightbulb className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
            <div>
              <p className="text-xs font-semibold text-amber-400 mb-1">Why Now</p>
              <p className="text-sm text-slate-300 leading-relaxed">{action.why_now}</p>
            </div>
          </div>
        </div>

        {/* Details Grid */}
        <div className="grid grid-cols-2 gap-4 mb-5">
          <div>
            <SectionLabel>Owner</SectionLabel>
            <div className="flex items-center gap-2">
              <User className="w-3.5 h-3.5 text-slate-500" />
              <p className="text-sm text-slate-200">{action.recommended_owner}</p>
            </div>
          </div>
          <div>
            <SectionLabel>Timing Window</SectionLabel>
            <div className="flex items-center gap-2">
              <Clock className="w-3.5 h-3.5 text-slate-500" />
              <p className="text-sm text-slate-200">{action.timing_window}</p>
            </div>
          </div>
          <div>
            <SectionLabel>Proof / Asset to Use</SectionLabel>
            <p className="text-sm text-slate-300">{action.proof_or_asset_to_use}</p>
          </div>
          <div>
            <SectionLabel>Expected Impact</SectionLabel>
            <p className="text-sm text-slate-300">{action.expected_commercial_impact}</p>
          </div>
        </div>

        {/* Risk of Delay */}
        <div className="rounded-lg bg-red-950/20 border border-red-800/30 p-3 mb-5">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
            <div>
              <p className="text-xs font-semibold text-red-400 mb-0.5">Risk of Delay</p>
              <p className="text-xs text-red-300/80">{action.risk_of_delay}</p>
            </div>
          </div>
        </div>

        {/* Alternative */}
        <div className="text-xs text-slate-500 mb-6">
          <span className="font-semibold">If blocked:</span> {action.alternative_action_if_blocked}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-3">
          <Button 
            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white h-11"
            onClick={onAccept}
            disabled={actionInProgress}
            data-testid="accept-btn"
          >
            <CheckCircle2 className="w-4 h-4 mr-2" />
            {actionInProgress ? "Processing..." : "Accept & Execute"}
          </Button>
          <Button 
            variant="outline" 
            className="border-slate-700 text-slate-400 hover:text-slate-300 h-11"
            onClick={onDismiss}
            disabled={actionInProgress}
            data-testid="dismiss-btn"
          >
            <X className="w-4 h-4 mr-2" />
            Dismiss
          </Button>
          {action.brief_id && (
            <Button 
              variant="ghost" 
              className="text-slate-500 hover:text-slate-300"
              onClick={() => navigate(`/commercial-briefs/${action.brief_id}`)}
            >
              <ArrowRight className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Compact card for secondary queue
  return (
    <div 
      className="p-4 rounded-lg bg-[#111827] border border-slate-800/60 hover:border-slate-700/60 transition-all"
      data-testid="queue-action-card"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className={`w-8 h-8 rounded-lg ${config.bg} border ${config.border} flex items-center justify-center shrink-0`}>
            <Icon className={`w-4 h-4 ${config.color}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <Badge className={`text-[9px] ${config.bg} ${config.color} ${config.border} border`}>
                {config.shortLabel}
              </Badge>
              <span className="text-xs text-slate-600">Priority: {action.priority_score}</span>
              <span className={`text-xs ${action.confidence_level === "High" ? "text-emerald-400" : action.confidence_level === "Medium" ? "text-amber-400" : "text-slate-500"}`}>
                {action.confidence_level}
              </span>
            </div>
            <p className="text-sm font-semibold text-slate-200 mb-1">{action.account_name}</p>
            <p className="text-xs text-slate-400 line-clamp-2">{action.recommended_action}</p>
            <p className="text-[10px] text-slate-500 mt-1">{action.timing_window}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button 
            size="sm"
            variant="ghost"
            className="h-8 px-2 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-900/20"
            onClick={onAccept}
            disabled={actionInProgress}
          >
            <CheckCircle2 className="w-4 h-4" />
          </Button>
          <Button 
            size="sm"
            variant="ghost"
            className="h-8 px-2 text-slate-500 hover:text-slate-400"
            onClick={onDismiss}
            disabled={actionInProgress}
          >
            <X className="w-4 h-4" />
          </Button>
          {action.brief_id && (
            <Button 
              size="sm"
              variant="ghost"
              className="h-8 px-2 text-slate-500 hover:text-slate-400"
              onClick={() => navigate(`/commercial-briefs/${action.brief_id}`)}
            >
              <ArrowRight className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function EmptyState({ selectedCategory, setSelectedCategory }) {
  const navigate = useNavigate();
  
  return (
    <div className="text-center py-20">
      <div className="w-16 h-16 rounded-full bg-emerald-900/20 border border-emerald-700/30 flex items-center justify-center mx-auto mb-4">
        <CheckCircle2 className="w-8 h-8 text-emerald-400" />
      </div>
      <h2 className="text-lg font-bold text-slate-100 mb-2" style={{ fontFamily: "Manrope, sans-serif" }}>
        {selectedCategory ? "No Actions in This Category" : "All Clear"}
      </h2>
      <p className="text-sm text-slate-500 max-w-md mx-auto">
        {selectedCategory 
          ? `No pending actions in the ${CATEGORY_CONFIG[selectedCategory]?.label || selectedCategory} category.`
          : "No action recommendations at this time. All briefs are either progressing well, on hold, or awaiting external events."}
      </p>
      <div className="flex items-center justify-center gap-3 mt-6">
        {selectedCategory && (
          <Button 
            variant="outline"
            className="border-slate-700 text-slate-300"
            onClick={() => setSelectedCategory("")}
          >
            View All Actions
          </Button>
        )}
        <Button 
          className="bg-slate-800 text-slate-200 hover:bg-slate-700"
          onClick={() => navigate("/commercial-briefs")}
        >
          View Commercial Briefs
        </Button>
      </div>
    </div>
  );
}
